// The supplier's order email and art email, right on the PO page — add or
// change them without leaving the PO (Stephanie 2026-11-02: "add back the
// fields to add the order and art email"). Saved on the supplier
// (PATCH /vendors/:id), so every PO to that supplier uses them.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";

type Field = "primary_email" | "art_email";

export function SupplierEmails({
  vendorId,
  orderEmail,
  artEmail,
  canEdit,
}: {
  vendorId: string;
  orderEmail: string | null;
  artEmail: string | null;
  canEdit: boolean;
}) {
  return (
    <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2 text-sm">
      <EmailField vendorId={vendorId} field="primary_email" label="Order email" value={orderEmail} canEdit={canEdit} />
      <EmailField vendorId={vendorId} field="art_email" label="Art email" value={artEmail} canEdit={canEdit} />
    </div>
  );
}

function EmailField({ vendorId, field, label, value, canEdit }: { vendorId: string; field: Field; label: string; value: string | null; canEdit: boolean }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (email: string | null) => api(`/vendors/${vendorId}`, { method: "PATCH", body: JSON.stringify({ [field]: email }) }),
    onSuccess: () => {
      setDraft(null);
      setError(null);
      void qc.invalidateQueries({ queryKey: ["vendors"] });
    },
    onError: (e) => setError(apiErrorMessage(e, "We couldn't save that email. Check it and try again.")),
  });

  if (draft !== null) {
    return (
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(draft.trim() || null);
        }}
      >
        <span className="text-muted">{label}</span>
        <input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
          aria-label={label}
          placeholder="name@supplier.com"
          className="h-8 w-64 rounded-md border border-rule bg-white px-2 text-sm text-ink"
        />
        <button type="submit" disabled={save.isPending} className="rounded-md bg-ink px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50">
          {save.isPending ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={() => { setDraft(null); setError(null); }} className="px-1 text-xs text-ink">Cancel</button>
        {error && <span className="w-full text-xs text-rust-600">{error}</span>}
      </form>
    );
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span className="text-muted">{label}</span>
      {value ? <span className="text-ink">{value}</span> : <span className="text-muted">None on file</span>}
      {canEdit && (
        <button type="button" onClick={() => setDraft(value ?? "")} className="inline-flex items-center gap-1 text-xs font-medium text-muted hover:text-ink">
          <Pencil className="h-3 w-3" aria-hidden /> {value ? "Edit" : "Add"}
        </button>
      )}
    </span>
  );
}
